/**
 * Visitor Tracker — captures referrer, UTM/campaign params, ad IDs, and device info
 * on every page load and stores them in localStorage. On login (or for authenticated
 * users), the accumulated data is flushed to the backend.
 */

const STORAGE_KEY = 'visitor_tracking';
const VISITOR_ID_KEY = 'visitor_id';

// ---------- Types ----------

export interface VisitRecord {
  id?: string;
  referrer_url: string | null;
  page_url: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  ad_id: string | null;
  ad_platform: string | null;
  device_resolution: string | null;
  os_name: string | null;
  os_version: string | null;
  browser_type: string | null;
  browser_version: string | null;
  visit_timestamp: string;
}

// ---------- Ad ID Detection ----------

const AD_PARAMS: Record<string, string> = {
  gclid: 'google',
  gbraid: 'google',
  wbraid: 'google',
  fbclid: 'facebook',
  msclkid: 'microsoft',
  li_fat_id: 'linkedin',
  ttclid: 'tiktok',
  rdt_cid: 'reddit',
  conversions_tracking: 'magsrv',
};

interface AdInfo {
  ad_id: string | null;
  ad_platform: string | null;
}

const detectAdId = (params: URLSearchParams): AdInfo => {
  for (const [param, platform] of Object.entries(AD_PARAMS)) {
    const value = params.get(param);

    if (value) return { ad_id: value, ad_platform: platform };
  }

  return { ad_id: null, ad_platform: null };
};

// ---------- UTM Params ----------

const getUTM = (params: URLSearchParams) => ({
  utm_source: params.get('utm_source'),
  utm_medium: params.get('utm_medium'),
  utm_campaign: params.get('utm_campaign'),
  utm_term: params.get('utm_term'),
  utm_content: params.get('utm_content'),
});

// ---------- Device & Browser Detection ----------

interface DeviceInfo {
  os_name: string | null;
  os_version: string | null;
  browser_type: string | null;
  browser_version: string | null;
}

type OsResult = Pick<DeviceInfo, 'os_name' | 'os_version'>;
type BrowserResult = Pick<DeviceInfo, 'browser_type' | 'browser_version'>;

const OS_PATTERNS: [RegExp, string, boolean][] = [
  [/Windows NT (\d+\.\d+)/, 'Windows', true],
  [/Mac OS X (\d+[._]\d+[._]?\d*)/, 'macOS', true],
  [/CrOS/, 'ChromeOS', false],
  [/Android (\d+\.?\d*)/, 'Android', true],
  [/iPhone OS (\d+_\d+)/, 'iOS', true],
  [/iPad.*OS (\d+_\d+)/, 'iOS', true],
  [/Linux/, 'Linux', false],
];

const detectOS = (ua: string): OsResult => {
  for (const [regex, name, hasVer] of OS_PATTERNS) {
    if (regex.test(ua)) return { os_name: name, os_version: hasVer ? RegExp.$1.replace(/_/g, '.') : null };
  }

  return { os_name: null, os_version: null };
};

const BROWSER_PATTERNS: [RegExp, string, RegExp | null][] = [
  [/Edg\/(\d+\.\d+)/, 'Edge', null],
  [/OPR\/(\d+\.\d+)/, 'Opera', null],
  [/Firefox\/(\d+\.\d+)/, 'Firefox', null],
];

const detectBrowser = (ua: string): BrowserResult => {
  for (const [regex, name] of BROWSER_PATTERNS) {
    if (regex.test(ua)) return { browser_type: name, browser_version: RegExp.$1 };
  }

  if (/Chrome\/(\d+\.\d+)/.test(ua) && !/Edg/.test(ua)) return { browser_type: 'Chrome', browser_version: RegExp.$1 };

  if (/Safari\//.test(ua) && !/Chrome/.test(ua)) return { browser_type: 'Safari', browser_version: /Version\/(\d+\.\d+)/.test(ua) ? RegExp.$1 : null };

  return { browser_type: null, browser_version: null };
};

const parseUserAgent = (): DeviceInfo => ({ ...detectOS(navigator.userAgent), ...detectBrowser(navigator.userAgent) });

const getDeviceResolution = (): string => `${window.screen.width}x${window.screen.height}`;

// ---------- Referrer ----------

const getReferrer = (): string | null => {
  const ref = document.referrer;

  if (!ref) return null;

  try {
    const canonicalHost = (host: string) => host.startsWith('www.') ? host.slice(4) : host;

    if (canonicalHost(new URL(ref).hostname) === canonicalHost(window.location.hostname)) return null;
  } catch {
    return null;
  }

  return ref;
};

// ---------- Storage Helpers ----------

const readVisits = (): VisitRecord[] => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');

    if (!Array.isArray(stored)) return [];

    // Upgrade old buffers in place. Stable IDs make retries safe without a new DB column.
    const visits = stored.filter(value => value && typeof value.page_url === 'string')
      .map(value => ({ ...value, id: value.id || createVisitorId() }));

    if (visits.some((value, index) => value.id !== stored[index]?.id)) writeVisits(visits);

    return visits;
  } catch {
    return [];
  }
};

const writeVisits = (visits: VisitRecord[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(visits));
  } catch {
    // localStorage full or blocked — silently drop
  }
};

const createVisitorId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `visitor_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
};

// ---------- Record Builder ----------

const buildRecord = (
  referrer: string | null,
  utm: ReturnType<typeof getUTM>,
  ad: AdInfo,
  device: DeviceInfo,
): VisitRecord => ({
  id: createVisitorId(),
  referrer_url: referrer,
  page_url: window.location.href,
  ...utm,
  ...ad,
  device_resolution: getDeviceResolution(),
  ...device,
  visit_timestamp: new Date().toISOString(),
});

// ---------- Public API ----------

/**
 * Capture the current visit and append to localStorage.
 * Records every page load — device info, referrer (if any), UTM, ad IDs.
 */
export const captureVisit = (): void => {
  const params = new URLSearchParams(window.location.search);
  const utm = getUTM(params);
  const ad = detectAdId(params);
  const referrer = getReferrer();
  const visits = readVisits();

  visits.push(buildRecord(referrer, utm, ad, parseUserAgent()));
  writeVisits(visits);
};

/**
 * Return accumulated visits without clearing.
 */
export const getVisits = (): VisitRecord[] => readVisits();

/**
 * Clear visits only after the backend confirms that it stored them.
 */
export const clearVisits = (acknowledged: VisitRecord[]): void => {
  const ids = new Set(acknowledged.map(visit => visit.id));

  try {
    writeVisits(readVisits().filter(visit => !ids.has(visit.id)));
  } catch {
    // Storage may be unavailable in privacy-restricted browser contexts.
  }
};

/**
 * Return GA4's real browser client ID from the first-party `_ga` cookie.
 * The backend uses this only to join automatic renewals to an existing GA
 * browser identity; it must never manufacture a replacement when unavailable.
 */
export const getGoogleAnalyticsClientId = (): string | null => {
  if (typeof document === 'undefined') return null;

  const cookie = document.cookie
    .split(';')
    .map(value => value.trim())
    .find(value => value.startsWith('_ga='));

  if (!cookie) return null;

  const parts = cookie.slice('_ga='.length).split('.');
  const firstNumber = parts[parts.length - 2];
  const secondNumber = parts[parts.length - 1];
  const containsOnlyDigits = (value: string): boolean => (
    value.split('').every(char => char >= '0' && char <= '9')
  );

  if (!firstNumber || !secondNumber) return null;
  if (!containsOnlyDigits(firstNumber) || !containsOnlyDigits(secondNumber)) return null;

  const clientId = `${firstNumber}.${secondNumber}`;

  return clientId.length <= 64 ? clientId : null;
};

/**
 * Stable anonymous visitor id for public analytics before login.
 */
export const getVisitorId = (): string => {
  try {
    const existing = localStorage.getItem(VISITOR_ID_KEY);

    if (existing) return existing;

    const visitorId = createVisitorId();

    localStorage.setItem(VISITOR_ID_KEY, visitorId);
    return visitorId;
  } catch {
    return createVisitorId();
  }
};
